import { useMemo } from 'react';
import { useSelector } from '@xstate/store/react';
import { useCanvasStores } from '../stores/CanvasContext';
import { useResourceById } from '../../stores';
import { getAttachedResourceIds } from '../entityTypes';
import type { CanvasEntity } from '../entityTypes';
import type { ResourceFieldType, ResourceAggregation } from '../../types';
import { computeAggregate } from '../../lib/formatFieldValue';

export interface CanvasUsage {
  canvasName: string;
  entityCount: number;
  entityNames: string[];
}

export interface AggregatedFieldStat {
  fieldId: string;
  key: string;
  type: ResourceFieldType;
  unit?: string;
  interval?: string;
  aggregate: ResourceAggregation;
  singleValue: number;       // value from the resource definition
  totalValue: number;         // aggregated across all attachments
  attachmentCount: number;
}

export interface ResourceBoardStats {
  totalEntityCount: number;
  totalCanvasCount: number;
  canvasUsages: CanvasUsage[];
  aggregatedFields: AggregatedFieldStat[];
}

function getEntityDisplayName(entity: CanvasEntity): string {
  const data = entity.data as Record<string, unknown>;
  if (entity.kind === 'task' && typeof data.taskId === 'string') return data.taskId;
  if (entity.kind === 'group' && typeof data.label === 'string') return data.label;
  if (entity.kind === 'textblock' && typeof data.text === 'string') return (data.text as string).slice(0, 30);
  if (entity.kind === 'textlabel' && typeof data.text === 'string') return (data.text as string).slice(0, 30);
  if (entity.kind === 'blocknode') return 'Block';
  return entity.kind;
}

export function useResourceBoardStats(resourceId: string | null): ResourceBoardStats {
  const { store } = useCanvasStores();
  const resource = useResourceById(resourceId ?? '');

  // Build a stable key from resource attachments.
  // Only changes when attachedResourceIds change, not on position updates.
  const attachmentKey = useSelector(store, s => {
    if (!resourceId) return '';
    let key = '';
    for (const e of Object.values(s.context.nodes)) {
      const ids = getAttachedResourceIds(e);
      if (ids.includes(resourceId)) key += `${e.id}:${e.kind}:${JSON.stringify(e.data)}|`;
    }
    for (const e of Object.values(s.context.groups)) {
      const ids = getAttachedResourceIds(e);
      if (ids.includes(resourceId)) key += `${e.id}:${e.kind}:${JSON.stringify(e.data)}|`;
    }
    return key;
  });

  return useMemo(() => {
    const empty: ResourceBoardStats = { totalEntityCount: 0, totalCanvasCount: 0, canvasUsages: [], aggregatedFields: [] };
    if (!resourceId || !resource || !attachmentKey) return empty;

    const ctx = store.getSnapshot().context;
    const canvasUsages: CanvasUsage[] = [];
    let totalEntityCount = 0;

    const entities = [
      ...Object.values(ctx.nodes),
      ...Object.values(ctx.groups),
    ];
    const matchingNames: string[] = [];

    for (const entity of entities) {
      const attachedIds = getAttachedResourceIds(entity);
      if (attachedIds.includes(resourceId)) {
        matchingNames.push(getEntityDisplayName(entity));
      }
    }

    if (matchingNames.length > 0) {
      canvasUsages.push({
        canvasName: ctx.canvas?.name ?? '',
        entityCount: matchingNames.length,
        entityNames: matchingNames,
      });
      totalEntityCount += matchingNames.length;
    }

    // Compute aggregated field values across all attachments
    const aggregatedFields: AggregatedFieldStat[] = [];
    for (const field of resource.fields) {
      if (field.type === 'text' || typeof field.value !== 'number') continue;
      if (field.aggregate === 'none' && totalEntityCount <= 1) continue;

      const singleValue = field.value;
      const values = Array(totalEntityCount).fill(singleValue) as number[];
      const totalValue = computeAggregate(
        field.aggregate === 'none' ? 'sum' : field.aggregate,
        values,
      );

      if (totalValue !== undefined) {
        aggregatedFields.push({
          fieldId: field.id,
          key: field.key,
          type: field.type,
          unit: field.unit,
          interval: field.interval,
          aggregate: field.aggregate,
          singleValue,
          totalValue,
          attachmentCount: totalEntityCount,
        });
      }
    }

    return {
      totalEntityCount,
      totalCanvasCount: canvasUsages.length,
      canvasUsages,
      aggregatedFields,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceId, resource, attachmentKey]);
}
